import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { CLIENT_DURATION_MAX_MINUTES, CLIENT_DURATION_MIN_MINUTES } from '@yourstore/domain';
import { occupiesLane, serviceRoleSides } from './calendar-grid';
import { useActions, useLive, useRuntime } from './context';
import { SupportSessionRefusal } from '../actions';
import { WriteRefusal, useSeat, useSeatRefusal } from './seat';
import { groupByCategory } from './menu-groups';
import { ClientPicker, pickedFrom } from './ClientPicker';
import type { PickedClient } from './ClientPicker';
import {
  formatClock,
  formatMenuPrice,
  isoDayIn,
  isoToZonedWallClock,
  zonedWallClockToIso,
} from '../mirror/time';
import type { Service, WireIdentity } from '../wire';
import { useLocale, useT } from '../messages';
import type { UiLocale } from '../messages';

export interface BookSheetPrefill {
  startIso?: string | undefined;
  serviceId?: string | undefined;
  resourceId?: string | undefined;
  clientId?: string | undefined;
  guestName?: string | undefined;
  source?: 'staff' | 'walk_in';
  /**
   * **What the last attempt asked for** (migration 0048) — the rebook path.
   *
   * "Find another time" after a collision books *anew*, so without these two
   * the owner would silently be offered the default length and none of the
   * add-ons she had already ticked, for a guest who has been told a price. They
   * are read off the rejected intent's own payload (`prefillFrom` in
   * `outbox/tasks.ts`), which is the only record of the ask that exists.
   */
  optionId?: string | undefined;
  addOnItemIds?: readonly string[] | undefined;
}

/**
 * One booking form, used by all three ways a booking starts: the walk-in button
 * on Today, a tap on a slot in Calendar, and "Reschedule" on a task.
 *
 * It is one component because they are one operation. A separate walk-in form
 * would drift from the calendar form, and the third caller — the task list —
 * would then have to choose which drift to inherit.
 *
 * Note what the form does **not** collect: an end time or a price. The catalog
 * supplies the price (B7) and the form shows the one it has mirrored in the
 * summary card at the bottom — text, never a field.
 *
 * **Laid out in the order she thinks it** (the 2026-09-30 redesign): the
 * service, found by name under her own menu headings; the length, as chips when
 * she sells it several ways, with a quiet "Adjust time" behind which the A1
 * stepper lives; what may be added; when (and where, only if there is a
 * choice); who; and one summary line of what is being sold. The button says
 * what it will do — "Book 10:45 AM – 11:45 AM" — and until it can, it is
 * disabled with the one thing still missing written beside it.
 *
 * **The length is a field, and it is the one thing on this sheet that is**
 * (A1; `docs/lovespa-discovery.md` §2.1). *"Your eyebrow wax takes me 15
 * minutes… hers takes me 35. Only waxing varies."* The owner is the person who
 * will be in the room; a form that would not let her say how long she needs
 * does not make the book more correct, it makes it wrong in ink. It is
 * prefilled from her remembered length for this guest where there is one, and
 * the sheet says which number it is using and what the menu says — "usually 35
 * min · menu 15" — so a prefill is never a silent decision.
 *
 * Only this surface has the field. The partner's agent still may not name a
 * span (v1.10 refuses one that is not what `/availability` offered) and the
 * guest site has no field at all, because neither of those callers is holding
 * the room.
 *
 * **It does not open for a seat that cannot write** (L15, `seat.tsx`). The
 * callers hide their doors — Today's walk-in button, the calendar's empty
 * stretch — and this is the wall behind them: a `readonly` seat or the
 * console's support look that reaches the sheet anyway (the task list's
 * *Find another time*, an inquiry's *Book this*) gets one sentence and a Close
 * button, never a form whose only ending is a refusal on submit. The check is
 * `can_write`, the server's word, not a reading of `role`.
 */
export function BookSheet({
  prefill,
  onDone,
  onCancel,
}: {
  prefill: BookSheetPrefill;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const ids = useId();

  const services = useLive(() => runtime.mirror.services(), []);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const clients = useLive(() => runtime.mirror.clients(), []);
  const { session, canWrite } = useSeat();
  const seatRefusal = useSeatRefusal();
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const [serviceId, setServiceId] = useState(prefill.serviceId ?? '');
  /** `''` is *"nobody has tapped a chip"*, which resolves to the default below. */
  const [optionId, setOptionId] = useState(prefill.optionId ?? '');
  const [addOnIds, setAddOnIds] = useState<readonly string[]>(prefill.addOnItemIds ?? []);
  const [resourceId, setResourceId] = useState(prefill.resourceId ?? '');
  /** The room, for a service that needs a person *and* a room (0056). `''` until chosen. */
  const [roomId, setRoomId] = useState('');
  const [startIso, setStartIso] = useState(prefill.startIso ?? '');
  const [clientId, setClientId] = useState(prefill.clientId ?? '');
  /**
   * A new client typed into the picker, not yet in the book. The booking
   * carries her as `guest` and the server adds her to the book in the same
   * write (`resolveGuest`), so nothing is queued ahead of it.
   */
  const [newGuest, setNewGuest] = useState<PickedClient | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const service = services.find((s) => s.service_id === serviceId);
  /**
   * **One lane or two** (spec §4.1.2, migration 0056). `sides` is `null` for
   * every service that needs one lane — the whole fleet until an owner flips
   * the Team screen's toggle — and the sheet is then exactly what it was: one
   * "Where" over everything that delivers the service. With two sides, "Who"
   * lists the people and a second control lists the rooms, and the booking
   * names both: the person as `resource_id`, the room in `also_resource_ids`.
   */
  const sides = service === undefined ? null : serviceRoleSides(service);
  const eligibleResources =
    service === undefined
      ? resources
      : sides === null
        ? resources.filter((r) => service.resource_ids.includes(r.resource_id))
        : resources.filter((r) => sides.people.includes(r.resource_id));
  const eligibleRooms =
    sides === null ? [] : resources.filter((r) => sides.rooms.includes(r.resource_id));
  /**
   * **"Where" is not a question when there is one answer.** A one-person spa
   * has one lane; asking her to choose it on every booking is a tap that can
   * only be answered one way. The single-lane case alone — a roled service's
   * "Who" and "Room" stay on screen, because the room a tap prefilled has to be
   * visible to be believed.
   */
  const soleResource =
    sides === null && eligibleResources.length === 1 ? eligibleResources[0] : undefined;
  const laneId = soleResource?.resource_id ?? resourceId;

  /**
   * **A tap in a room's column prefills the room, not "Who"** (0056). The
   * calendar hands this sheet one `resourceId` — the column that was tapped —
   * and until the service is chosen the sheet cannot know whether that lane is
   * a person or a room. So the moment a roled service is in hand, a prefilled
   * lane that sits on the rooms side moves over to the Room control, and "Who"
   * opens on "Choose…" rather than falling back to it silently. Idempotent:
   * once moved, `resourceId` is empty and there is nothing to move again.
   */
  useEffect(() => {
    if (sides === null || resourceId === '' || !sides.rooms.includes(resourceId)) return;
    setRoomId(resourceId);
    setResourceId('');
  }, [sides, resourceId]);
  const client = clients.find((c) => c.client_id === clientId);

  /* ---------------------------------------------------------------- */
  /* The ways it is sold, and what may be attached (migration 0048)    */
  /* ---------------------------------------------------------------- */

  /**
   * `?? []` on both, always: a `services` row this device mirrored before these
   * fields existed is read back from IndexedDB without them whatever the type
   * says (`wire.ts`, `Service`). An inactive option or a paused link is dropped
   * here rather than drawn greyed — this sheet is what may be *sold*.
   */
  const options = (service?.options ?? []).filter((o) => o.active);
  const addOns = (service?.add_ons ?? []).filter((a) => a.active);

  /**
   * **Absent is the default, and there is no "no option" state** (design §8.3).
   *
   * The one rule, said here the way the engine's `resolveOption` says it: the
   * one she marked, else the first in her order. A sheet that let the owner
   * un-choose would be offering a state no write path can record.
   */
  const defaultOption = options.find((o) => o.is_default) ?? options[0];
  const option = options.find((o) => o.option_id === optionId) ?? defaultOption;

  const chosenAddOns = addOns.filter((a) => addOnIds.includes(a.service_id));
  /** Minutes **add** and the option **replaces** — the server's one arithmetic. */
  const addOnMinutes = chosenAddOns.reduce((total, a) => total + a.duration_min, 0);
  /**
   * What the catalog says this comes to: the option's price plus each add-on's
   * *attached* price. Shown in the summary card as the menu's — never a field,
   * and never sent (B7).
   */
  const baseCents = option?.price_cents ?? service?.price_cents ?? 0;
  const catalogCents = baseCents + chosenAddOns.reduce((total, a) => total + a.price_cents, 0);

  /* ---------------------------------------------------------------- */
  /* Length (A1)                                                       */
  /* ---------------------------------------------------------------- */

  /**
   * What she is remembered as for *this* service, out of the mirror.
   *
   * `?? []` because a `clients` row this device wrote before the field existed
   * is read back from IndexedDB without it (`wire.ts`, `ClientList`).
   *
   * **Matched on `(service, option)`, and a named option wins** (migration
   * 0048). That precedence is not this screen's invention: it is what
   * `client_service_durations`' two partial unique indexes encode — one row per
   * `(client, item)` where `option_id is null`, one row per `(client, item,
   * option)` where it is not — so a guest has at most one broad answer and at
   * most one answer per length, and the narrow one is the more specific fact.
   * The engine resolves the same way in `effectiveDurationMin`, which is what
   * makes the prefill the number the server will reach.
   *
   * The `null`-option row means *"this service however it is sold"* and applies
   * to every chip; it is also what every row written before 0048 says, so a
   * service sold one way behaves exactly as it did.
   */
  const rememberedForService = (client?.durations ?? []).filter((d) => d.service_id === serviceId);
  const remembered =
    rememberedForService.find((d) => d.option_id !== null && d.option_id === option?.option_id) ??
    rememberedForService.find((d) => d.option_id === null);
  /**
   * The number the sheet starts on — **the base span**, before add-ons.
   *
   * The precedence is the server's, repeated rather than re-derived: a typed
   * length wins, then hers, then the chosen option's, then the item's. That is
   * exactly `effectiveDurationMin(service, party, override, option)`, where the
   * override is passed *outside* the option and therefore beats it — so the
   * number on screen is the number the resolver will reach, which is what makes
   * "send nothing when she left it alone" true.
   *
   * An option with no span of its own (a dish's *Large*) falls through to the
   * item's, which is what the catalog means by omitting it.
   */
  const suggested = remembered?.duration_min ?? option?.duration_min ?? service?.duration_min;

  /**
   * `null` means *"whatever the suggestion is"*, not zero.
   *
   * Keeping "untouched" as its own state rather than seeding the field with a
   * number is what lets the mirror arrive late — services hydrate a tick after
   * this sheet mounts — without the control freezing on whatever it knew first.
   */
  const [minutes, setMinutes] = useState<number | null>(null);
  /** `null` means "nobody has decided", and the default below applies. */
  const [rememberChoice, setRememberChoice] = useState<boolean | null>(null);
  /**
   * Whether the stepper is drawn. The length is still hers to set on every
   * booking (A1) — it is one quiet tap away rather than four buttons that are
   * on screen for the nine bookings in ten that take the menu's length.
   */
  const [adjusting, setAdjusting] = useState(false);

  /**
   * A different service, or a different guest, is a different question — so the
   * typed length and the remember choice are dropped and the suggestion applies
   * again. Deliberately **not** keyed on `suggested`: that recomputes on every
   * mirror revision, and an effect that watched it would wipe a half-typed
   * number the moment an unrelated SSE frame landed.
   */
  useEffect(() => {
    setMinutes(null);
    setRememberChoice(null);
    setAdjusting(false);
  }, [serviceId, clientId]);

  const effectiveMinutes = minutes ?? suggested ?? 0;
  /** A length that is not what the resolver would have picked on its own. */
  const lengthDiffers = suggested !== undefined && effectiveMinutes !== suggested;
  const lengthValid =
    Number.isInteger(effectiveMinutes) &&
    effectiveMinutes >= CLIENT_DURATION_MIN_MINUTES &&
    effectiveMinutes <= CLIENT_DURATION_MAX_MINUTES;

  /**
   * **On by default when there is somebody to remember it about.**
   *
   * A walk-in has no `clients` row, so there is nothing to attach a length to
   * and the checkbox is not drawn at all. For a named guest the default is on,
   * because the owner who just typed 35 for Susie almost always means *Susie is
   * a 35* — and unticking it sticks, which is what `rememberChoice` holds.
   */
  const canRemember = clientId !== '' && lengthDiffers && lengthValid;
  const remember = canRemember && (rememberChoice ?? true);

  /**
   * **How long the room is actually taken.** Arithmetic, not a rule: the base
   * is what the one resolver decided and the add-ons' minutes are a sum the
   * server performs identically (`addOnMinutes` in `@yourstore/scheduling`).
   * Nothing here re-derives a span — dev.md rule 3 is intact.
   */
  const totalMinutes = effectiveMinutes + addOnMinutes;

  /** A chip, or a tick, is a different question — the typed length goes back. */
  const forgetTypedLength = (): void => {
    setMinutes(null);
    setRememberChoice(null);
    setAdjusting(false);
  };

  /**
   * **What the menu says for the length that is chosen** — the option's span,
   * else the item's. The number an adjusted length is compared against in the
   * one line she reads ("90 min booked · the menu says 120").
   */
  const menuMinutes = option?.duration_min ?? service?.duration_min;
  /**
   * The line under the length, or none. Where a number came from is always on
   * screen when it is not simply the menu's (A1: a prefill is never a silent
   * decision), and an adjusted length says what the menu says beside it.
   */
  const lengthNote: string | null =
    service === undefined
      ? null
      : minutes !== null && menuMinutes !== undefined && effectiveMinutes !== menuMinutes
        ? t('book.lengthBooked', { minutes: effectiveMinutes, menu: menuMinutes })
        : minutes === null && remembered !== undefined
          ? // When the row she matched names a length, the sentence says
            // which — "usually 105 min for her · 90 minutes, menu 90": her
            // number, the length it is about, and what that length takes for
            // everybody else.
            remembered.option_name === null
            ? t('book.lengthFromClient', {
                minutes: remembered.duration_min,
                catalog: remembered.catalog_duration_min,
              })
            : t('book.lengthFromClientOption', {
                minutes: remembered.duration_min,
                catalog: remembered.catalog_duration_min,
                option: remembered.option_name,
              })
          : // With chips on screen the chosen chip already says the length.
            options.length > 1
            ? null
            : t('book.lengthPlain', { minutes: effectiveMinutes });

  const step = (by: number): void =>
    setMinutes((current) =>
      Math.min(
        CLIENT_DURATION_MAX_MINUTES,
        Math.max(CLIENT_DURATION_MIN_MINUTES, (current ?? suggested ?? 0) + by),
      ),
    );

  /* ---------------------------------------------------------------- */
  /* What this device can already see in the way                       */
  /* ---------------------------------------------------------------- */

  /**
   * An overlap **in the mirror**, recomputed whenever the ask changes — and the
   * length is part of the ask, which is why it exists at all: typing 35 where
   * the menu says 15 is exactly the edit that runs a booking into the next one,
   * and finding out at the counter beats finding out in the task list.
   *
   * It is a warning and never a gate, and the wording says which. `CalendarView`
   * draws the line this follows: an empty cell in the mirror means "nothing is
   * booked in *this device's* copy of the book", not "this is free" — only
   * `packages/scheduling` knows about buffers, holds, blackouts and capacity,
   * and there is one implementation of it (dev.md rule 3). So the server still
   * decides, the enriched `409` still becomes a task, and the owner may book
   * straight through this line if she knows something the iPad does not.
   */
  const day = startIso === '' ? '' : isoDayIn(timezone, startIso);
  const dayRows = useLive(async () => (day === '' ? [] : await runtime.mirror.day(day)), [], [day]);
  const clash =
    startIso === '' || laneId === '' || !lengthValid
      ? undefined
      : dayRows.find((row) => {
          // Either lane taken is a clash: the room's book counts as much as the
          // person's, which is the whole reason the room is a lane.
          if (!occupiesLane(row, laneId) && (roomId === '' || !occupiesLane(row, roomId))) {
            return false;
          }
          if (row.status === 'cancelled' || row.status === 'no_show') return false;
          const from = Date.parse(startIso);
          // The **whole** span, add-ons included: fifteen minutes of
          // dermaplaning is fifteen minutes of the room, and a warning that
          // measured only the base would go quiet exactly when the booking got
          // longer.
          const to = from + totalMinutes * 60_000;
          // Half-open `[start, end)` — the same rule the exclusion constraint
          // uses, so "this says clash" and "the server says clash" agree about
          // the boundary case of one ending exactly as the next begins.
          return Date.parse(row.slot.start) < to && from < Date.parse(row.slot.end);
        });

  /* ---------------------------------------------------------------- */
  /* When, and what is still missing                                   */
  /* ---------------------------------------------------------------- */

  /**
   * "10:45 AM – 12:45 PM" — the start she chose and the end the whole span
   * makes, add-ons included. Arithmetic on the one number the sheet already
   * shows, not a rule: the server's end is `start + span` too.
   */
  const timeRange =
    startIso === '' || !lengthValid || service === undefined
      ? ''
      : t('book.timeRange', {
          from: formatClock(startIso, timezone, locale),
          to: formatClock(
            new Date(Date.parse(startIso) + totalMinutes * 60_000).toISOString(),
            timezone,
            locale,
          ),
        });
  /**
   * The zone is only worth a word when this device is somewhere else — an
   * owner on holiday, or a laptop in another state. On the iPad in the shop the
   * two agree and the line would be noise.
   */
  const zoneDiffers = deviceTimeZone() !== timezone;

  const resource = eligibleResources.find((r) => r.resource_id === laneId);
  const room = eligibleRooms.find((r) => r.resource_id === roomId);
  const namedClient = client !== undefined;
  const guestName = newGuest?.draft?.name.trim() ?? '';
  const walkIn = !namedClient && guestName !== '';
  const identities: WireIdentity[] = newGuest?.draft?.identities ?? [];

  /**
   * **The one thing still missing, in plain words, or `null`.** The button is
   * disabled while there is one and the sentence sits beside it, so the sheet
   * never offers a tap whose only answer is a refusal. The order is the
   * sheet's own, top to bottom.
   */
  const missing: string | null =
    service === undefined
      ? t('book.errorService')
      : resource === undefined
        ? t('book.errorWhere')
        : sides !== null && room === undefined
          ? t('book.errorRoom')
          : startIso === ''
            ? t('book.errorTime')
            : !namedClient && !walkIn
              ? t('book.errorName')
              : // `resolveGuest` in `core-api` refuses a booking with no way to
                // reach the guest, and says why. Refusing here as well means the
                // owner finds out at the counter rather than an hour later in
                // the task list.
                walkIn && identities.length === 0
                ? t('book.errorContact')
                : !lengthValid
                  ? t('book.errorLength')
                  : null;

  const submit = async (): Promise<void> => {
    if (missing !== null) return setError(missing);
    // Narrowed again for the compiler; `missing` already said all of this.
    if (service === undefined || resource === undefined) return;

    try {
      await actions.book({
        serviceId: service.service_id,
        serviceName: service.name,
        // The optimistic row's span, so the Today list shows the length that was
        // actually asked for rather than the menu's while the intent is queued —
        // **with the add-ons in it**, because that is what the server will write.
        durationMin: totalMinutes,
        // Sent only when it is a *decision*. An untouched control means "the
        // server decides", which is what this request has always meant — and the
        // server's resolver reaches the same number, so sending it back would
        // record a deliberate override of nothing. The comparison is against the
        // **base**: the add-ons' minutes are the server's own addition and were
        // never a thing anybody typed.
        ...(lengthDiffers ? { explicitDurationMin: effectiveMinutes } : {}),
        ...(remember ? { rememberDuration: true } : {}),
        // **Ids, never numbers** (B7). Sent whenever this service is sold in more
        // than one way, even when nobody touched a chip: the chosen one is then
        // the default, and saying so explicitly is what makes the queued row and
        // the server's row describe the same sale. A service sold one way sends
        // nothing at all, so its request is byte for byte the one it always was.
        ...(option === undefined ? {} : { optionId: option.option_id }),
        ...(chosenAddOns.length === 0
          ? {}
          : { addOnItemIds: chosenAddOns.map((a) => a.service_id) }),
        // What the menu comes to, for the optimistic row only — so Today and the
        // till read the truth while the intent is queued. It is never on the
        // payload; `actions.book` is where that is enforced.
        catalogPriceCents: catalogCents,
        ...(option === undefined ? {} : { optionName: option.name }),
        addOns: chosenAddOns.map((a) => ({
          service_id: a.service_id,
          name: a.name,
          price_cents: a.price_cents,
          duration_min: a.duration_min,
        })),
        resourceId: resource.resource_id,
        resourceName: resource.name,
        // The room, only for a service that needs one: a single-lane request
        // stays byte for byte what it was.
        ...(room === undefined ? {} : { alsoResourceIds: [room.resource_id] }),
        startIso,
        source: prefill.source ?? (walkIn ? 'walk_in' : 'staff'),
        notes: notes.trim() === '' ? undefined : notes.trim(),
        clientName: namedClient ? (client?.display_name ?? '') : guestName,
        ...(namedClient ? { clientId } : { guest: { name: guestName, identities } }),
      });
    } catch (e) {
      // The outbox's typed refusal is a sentence for the person in front of
      // the sheet, not a rejection for the console; anything else is still a
      // fault and is rethrown for the log.
      if (!(e instanceof SupportSessionRefusal)) throw e;
      setError(e.message);
      return;
    }
    onDone();
  };

  // `session === null` is a cold open still reading IndexedDB, not a seat that
  // may not write; the form draws and the submit is guarded like every other
  // intent. Only a known seat that cannot write gets the wall.
  if (session !== null && !canWrite) {
    return (
      <div className="sheet" role="dialog" aria-label={t('book.dialogLabel')}>
        <div className="sheet__panel">
          <h2>{t('book.title')}</h2>
          <WriteRefusal message={seatRefusal} />
          <div className="sheet__actions">
            <button type="button" className="button" onClick={onCancel}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet" role="dialog" aria-label={t('book.dialogLabel')}>
      <div className="sheet__panel book-sheet">
        <h2>{t('book.title')}</h2>

        <ServicePicker
          services={services}
          value={serviceId}
          currency={currency}
          locale={locale}
          onChange={(nextId) => {
            setServiceId(nextId);
            // A lane the calendar prefilled — the column that was tapped —
            // survives the service pick when that service still delivers it,
            // and only then: a room this service is never in would be an
            // answer to a question nobody asked. The roled case moves a
            // surviving room over to the Room control (the effect above).
            const next = services.find((s) => s.service_id === nextId);
            if (next === undefined || !next.resource_ids.includes(resourceId)) {
              setResourceId('');
            }
            // A different service is a different set of lengths and a
            // different set of attachments; keeping either would carry an id
            // that belongs to something else, which the server refuses by
            // name and the owner would have no way to see.
            setOptionId('');
            setAddOnIds([]);
          }}
        />

        {/*
         * **Length** (A1). The lengths she sells it in, as one segmented row
         * of chips — they are the menu's answers, and each carries its price
         * because *"is the 90 two-fifty-five?"* is the question they exist to
         * stop. A service sold one way draws no control at all: its length is
         * a fact, stated on one line.
         *
         * `aria-pressed` rather than radios: they behave as a segmented control
         * and a screen reader should hear which is chosen.
         *
         * The stepper is behind "Adjust time", not gone: she sets each
         * booking's length herself whenever the room needs it, and the rest of
         * A1 — the remembered length, the remember box, the differs-from-menu
         * line — works exactly as before once it is open.
         */}
        {service !== undefined ? (
          <div className="field book-length" role="group" aria-labelledby={`${ids}-length`}>
            <span id={`${ids}-length`}>{t('book.length')}</span>
            {options.length > 1 ? (
              <div className="segmented">
                {options.map((o) => {
                  const chosen = o.option_id === option?.option_id;
                  return (
                    <button
                      key={o.option_id}
                      type="button"
                      className={`segmented__option${chosen ? ' is-chosen' : ''}`}
                      aria-pressed={chosen}
                      onClick={() => {
                        setOptionId(o.option_id);
                        forgetTypedLength();
                      }}
                    >
                      {o.duration_min === null
                        ? t('book.optionChip', {
                            name: o.name,
                            price: formatMenuPrice(o.price_cents, currency, locale),
                          })
                        : t('book.lengthChip', {
                            minutes: o.duration_min,
                            price: formatMenuPrice(o.price_cents, currency, locale),
                          })}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="book-length__line">
              {lengthNote === null ? null : <span className="book-length__note">{lengthNote}</span>}
              {adjusting ? null : (
                <button type="button" className="link-button" onClick={() => setAdjusting(true)}>
                  {t('book.adjustTime')}
                </button>
              )}
            </div>
            {adjusting ? (
              <div className="book-length__adjust">
                {/*
                 * Steppers first because that is how the number is actually
                 * changed at a counter — a thumb on ±5 beats a keyboard — with
                 * direct entry for the case the steppers would take six taps to
                 * reach. `inputMode="numeric"` rather than `type="number"`, so
                 * an iPad shows the number pad without the spinner arrows.
                 */}
                <div className="stepper">
                  <button
                    type="button"
                    className="stepper__step"
                    aria-label={t('book.lengthMinus', { by: 15 })}
                    onClick={() => step(-15)}
                  >
                    −15
                  </button>
                  <button
                    type="button"
                    className="stepper__step"
                    aria-label={t('book.lengthMinus', { by: 5 })}
                    onClick={() => step(-5)}
                  >
                    −5
                  </button>
                  <input
                    aria-label={t('book.minutes')}
                    inputMode="numeric"
                    value={String(effectiveMinutes)}
                    onChange={(e) => {
                      const next = Number(e.target.value.replace(/[^0-9]/g, ''));
                      setMinutes(Number.isNaN(next) ? 0 : next);
                    }}
                  />
                  <button
                    type="button"
                    className="stepper__step"
                    aria-label={t('book.lengthPlus', { by: 5 })}
                    onClick={() => step(5)}
                  >
                    +5
                  </button>
                  <button
                    type="button"
                    className="stepper__step"
                    aria-label={t('book.lengthPlus', { by: 15 })}
                    onClick={() => step(15)}
                  >
                    +15
                  </button>
                  {minutes === null ? null : (
                    <button type="button" className="link-button" onClick={forgetTypedLength}>
                      {t('book.adjustReset')}
                    </button>
                  )}
                </div>
                {canRemember ? (
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRememberChoice(e.target.checked)}
                    />
                    <span>
                      {t('book.rememberLength', {
                        minutes: effectiveMinutes,
                        name: client?.display_name ?? '',
                        service: service.name,
                      })}
                    </span>
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/*
         * **What may be added to it** (migration 0048) — only once there is a
         * parent to add it to, which is what an attach-only treatment is.
         * Each box carries the *attached* numbers, which are not the add-on's
         * own: dermaplaning is $85 alone and $45 on a facial, and a box showing
         * the standalone price would be quoting the wrong one.
         */}
        {addOns.length === 0 ? null : (
          <div className="field" role="group" aria-labelledby={`${ids}-addons`}>
            <span id={`${ids}-addons`}>{t('book.addOns')}</span>
            {addOns.map((a) => (
              <label className="check-row" key={a.service_id}>
                <input
                  type="checkbox"
                  checked={addOnIds.includes(a.service_id)}
                  onChange={(e) => {
                    setAddOnIds((current) =>
                      e.target.checked
                        ? [...current, a.service_id]
                        : current.filter((id) => id !== a.service_id),
                    );
                  }}
                />
                <span>
                  {t('book.addOnChoice', {
                    name: a.name,
                    price: formatMenuPrice(a.price_cents, currency, locale),
                    minutes: a.duration_min,
                  })}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="book-sheet__when">
          <label className="field">
            <span>{t('book.starts')}</span>
            <input
              type="datetime-local"
              value={isoToZonedWallClock(startIso, timezone)}
              onChange={(e) => setStartIso(zonedWallClockToIso(e.target.value, timezone))}
            />
            {timeRange === '' ? null : (
              <small className="book-sheet__range">
                {timeRange}
                {zoneDiffers ? ` · ${t('book.zoneNote', { timezone })}` : ''}
              </small>
            )}
          </label>

          {soleResource !== undefined ? null : (
            <label className="field">
              <span>{sides === null ? t('book.where') : t('book.who')}</span>
              <select value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
                <option value="">{t('book.choose')}</option>
                {eligibleResources.map((r) => (
                  <option key={r.resource_id} value={r.resource_id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {sides === null ? null : (
            <label className="field">
              <span>{t('book.room')}</span>
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                <option value="">{t('book.choose')}</option>
                {eligibleRooms.map((r) => (
                  <option key={r.resource_id} value={r.resource_id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {/*
         * **Who it is for** — the shared picker (the gift card sheet's): search
         * her book by name, phone digits or email, or add someone new inline.
         * A new client rides on the booking as `guest`; a picked one as
         * `client_id`.
         */}
        <ClientPicker
          label={t('book.guest')}
          value={newGuest ?? (client === undefined ? null : pickedFrom(client))}
          initialSearch={prefill.guestName ?? ''}
          onChange={(v) => {
            if (v === null) {
              setNewGuest(null);
              setClientId('');
            } else if (v.draft !== undefined) {
              setNewGuest(v);
              setClientId('');
            } else {
              setNewGuest(null);
              setClientId(v.clientId);
            }
          }}
        />

        <label className="field">
          <span>{t('common.notes')}</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </label>

        {/*
         * **What is being sold, at the menu's prices** (B7). Text, never a
         * field: the price comes from the catalog and the server prices the
         * booking again from the ids alone.
         */}
        {service === undefined ? null : (
          <section className="book-summary" aria-label={t('book.summaryLabel')}>
            <p className="book-summary__line">
              {t('book.summaryLine', {
                name:
                  option !== undefined && options.length > 1 && option.duration_min === null
                    ? `${service.name} (${option.name})`
                    : service.name,
                minutes: effectiveMinutes,
                price: formatMenuPrice(baseCents, currency, locale),
              })}
            </p>
            {chosenAddOns.map((a) => (
              <p className="book-summary__addon" key={a.service_id}>
                {t('book.summaryAddOn', {
                  name: a.name,
                  minutes: a.duration_min,
                  price: formatMenuPrice(a.price_cents, currency, locale),
                })}
              </p>
            ))}
            {chosenAddOns.length === 0 ? null : (
              <p className="book-summary__total">
                {t('book.summaryTotal', {
                  minutes: totalMinutes,
                  price: formatMenuPrice(catalogCents, currency, locale),
                })}
              </p>
            )}
          </section>
        )}

        {clash !== undefined ? (
          <p className="warning">
            {t('book.clash', {
              name: clash.client.display_name,
              clock: formatClock(clash.slot.start, timezone, locale),
            })}
          </p>
        ) : null}

        {error !== null ? <p className="error">{error}</p> : null}

        <div className="sheet__actions book-sheet__actions">
          {missing === null ? null : <p className="book-sheet__missing">{missing}</p>}
          <button type="button" className="button button--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={missing !== null}
            onClick={() => void submit()}
          >
            {timeRange === '' ? t('book.submit') : t('book.submitRange', { range: timeRange })}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The device's own zone, or `null` when this engine will not say. */
function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/** Case- and accent-blind, so "masaje" finds "Masaje" and "facial" finds "Faciál". */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * What a service costs across the lengths she sells it in — "$85–$340" — or
 * `null` when it is sold one way. The picker shows **names**: a headline length
 * beside a service sold in four lengths is a number that is wrong three times
 * in four, which is exactly what the old list did.
 */
function priceSpan(
  s: Service,
  currency: string,
  locale: UiLocale,
  t: ReturnType<typeof useT>,
): string | null {
  const live = (s.options ?? []).filter((o) => o.active);
  if (live.length < 2) return null;
  const prices = live.map((o) => o.price_cents);
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  return low === high
    ? formatMenuPrice(low, currency, locale)
    : t('book.priceRange', {
        from: formatMenuPrice(low, currency, locale),
        to: formatMenuPrice(high, currency, locale),
      });
}

/**
 * **Her menu, under her headings, in her order** — the picker.
 *
 * The rows arrive sorted by her `sort_order` (`Mirror.services`) and are grouped
 * by `groupByCategory`, the same helper the Settings menu uses, so the list she
 * books from reads like the card she edits. Retired services never appear, and
 * **attach-only treatments are not in it** (migration 0048): they are "only
 * sold added", and the server refuses one booked alone — the refusal belongs
 * here, at the picker, rather than as a `400` after she has told somebody a
 * price. They appear under "Add to this booking" once a parent is chosen.
 *
 * Once a service is chosen the list folds to one row with "Change"; a sheet
 * opened on a known service (the rebook path) opens folded.
 */
function ServicePicker({
  services,
  value,
  currency,
  locale,
  onChange,
}: {
  services: readonly Service[];
  value: string;
  currency: string;
  locale: UiLocale;
  onChange: (serviceId: string) => void;
}): ReactNode {
  const t = useT();
  const ids = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const sellable = services.filter((s) => s.active && !s.attach_only);
  const chosen = sellable.find((s) => s.service_id === value);
  const needle = fold(query.trim());
  const matches =
    needle === ''
      ? sellable
      : sellable.filter(
          (s) => fold(s.name).includes(needle) || fold(s.category ?? '').includes(needle),
        );

  return (
    <div className="field service-picker" role="group" aria-labelledby={`${ids}-label`}>
      <span id={`${ids}-label`}>{t('book.service')}</span>
      {chosen !== undefined && !open ? (
        <button
          type="button"
          className="service-picker__chosen"
          onClick={() => setOpen(true)}
          aria-label={`${chosen.name}, ${t('book.changeService')}`}
        >
          <span className="service-picker__name">{chosen.name}</span>
          <span className="service-picker__change">{t('book.changeService')}</span>
        </button>
      ) : (
        <>
          <input
            type="search"
            className="service-picker__search"
            aria-label={t('book.findService')}
            placeholder={t('book.findService')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="service-picker__list">
            {matches.length === 0 && needle !== '' ? (
              <p className="muted service-picker__empty">
                {t('book.noServiceMatch', { query: query.trim() })}
              </p>
            ) : null}
            {groupByCategory(matches).map(([heading, rows]) => (
              <div
                key={heading ?? ''}
                className="service-picker__group"
                role={heading === null ? undefined : 'group'}
                aria-label={heading ?? undefined}
              >
                {heading === null ? null : <h3 className="service-picker__heading">{heading}</h3>}
                {rows.map((s) => {
                  const span = priceSpan(s, currency, locale, t);
                  const isChosen = s.service_id === value;
                  return (
                    <button
                      key={s.service_id}
                      type="button"
                      className={`service-picker__item${isChosen ? ' is-chosen' : ''}`}
                      aria-pressed={isChosen}
                      aria-label={span === null ? s.name : `${s.name}, ${span}`}
                      onClick={() => {
                        if (!isChosen) onChange(s.service_id);
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      <span className="service-picker__name">{s.name}</span>
                      {span === null ? null : <span className="service-picker__price">{span}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
